import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Download, Share2, Link2, Mail, Copy, Check, X } from 'lucide-react';
import { useAuth } from '../../../../AuthProvider';

const ExportShareMenu = ({ onClose, discoverState }) => {
  const { getAccessToken } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [includeMap, setIncludeMap] = useState(true);
  const [includeCards, setIncludeCards] = useState(true);
  const [shareLink, setShareLink] = useState('');
  const [linkCopied, setLinkCopied] = useState(false);
  const [allowDownload, setAllowDownload] = useState(true);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [linkError, setLinkError] = useState(null);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [pdfError, setPdfError] = useState(null);

  const handleExport = async () => {
    if (!discoverState) {
      setPdfError('No discover state available. Please perform a search first.');
      return;
    }

    setPdfGenerating(true);
    setPdfError(null);

    try {
      const accessToken = getAccessToken();
      const response = await fetch(
        `${process.env.REACT_APP_NODE_URL}/discover/pdf/generate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
          },
          body: JSON.stringify({
            discoverState,
          }),
        },
      );

      const data = await response.json();

      if (!response.ok || !data.status) {
        throw new Error(data.message || 'Failed to generate PDF');
      }

      if (data.link) {
        window.open(data.link, '_blank');
        onClose();
      } else {
        throw new Error('PDF link not received');
      }
    } catch (err) {
      console.error('Error generating PDF:', err);
      setPdfError(err.message || 'Failed to generate PDF report');
    } finally {
      setPdfGenerating(false);
    }
  };

  const handleGenerateLink = async () => {
    if (!discoverState) {
      setLinkError('No discover state available. Please perform a search first.');
      return;
    }

    setGeneratingLink(true);
    setLinkError(null);

    try {
      const accessToken = getAccessToken();
      const response = await fetch(
        `${process.env.REACT_APP_NODE_URL}/discover/share-link`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            discoverState,
            allowDownload,
          }),
        },
      );

      const data = await response.json();

      if (!response.ok || !data.status) {
        throw new Error(data.message || 'Failed to generate share link');
      }

      setShareLink(data.shareLink);
    } catch (error) {
      console.error('Error generating share link:', error);
      setLinkError(error.message || 'Failed to generate share link. Please try again.');
    } finally {
      setGeneratingLink(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareLink);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth='sm'
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '0.75rem',
        },
      }}
    >
      <DialogTitle>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
            Export & Share
          </Typography>
          <IconButton onClick={onClose} size='small'>
            <X size={20} />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ padding: 0 }}>
        <Box sx={{ borderBottom: '1px solid #E5E7EB' }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                color: '#6B7280',
                '&.Mui-selected': {
                  color: '#39585C',
                },
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#39585C',
              },
            }}
          >
            <Tab
              icon={<Download size={16} />}
              iconPosition='start'
              label='Export'
              sx={{ textTransform: 'none', flex: 1 }}
            />
            <Tab
              icon={<Share2 size={16} />}
              iconPosition='start'
              label='Share'
              sx={{ textTransform: 'none', flex: 1 }}
            />
          </Tabs>
        </Box>

        <Box
          sx={{
            padding: { xs: '1rem', md: '1.5rem' },
            maxHeight: '60vh',
            overflowY: 'auto',
          }}
        >
          {activeTab === 0 && (
            <Box
              sx={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: '1.125rem',
                    fontWeight: 600,
                    marginBottom: '0.5rem',
                  }}
                >
                  Export Report
                </Typography>
                <Typography sx={{ fontSize: '0.875rem', color: '#6B7280' }}>
                  Download your analysis as a PDF or data file
                </Typography>
              </Box>

              <Box>
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    marginBottom: '0.25rem',
                  }}
                >
                  Export Format
                </Typography>
                <Typography sx={{ fontSize: '0.875rem', color: '#6B7280' }}>
                  PDF Report
                </Typography>
              </Box>

              <Box sx={{ marginTop: '0.5rem' }}>
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    marginBottom: '0.25rem',
                  }}
                >
                  Page Layout
                </Typography>
                <Typography sx={{ fontSize: '0.875rem', color: '#6B7280' }}>
                  Portrait
                </Typography>
              </Box>

              <Box>
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    marginBottom: '0.75rem',
                  }}
                >
                  Include in Report
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.5rem',
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={includeMap}
                        onChange={e => setIncludeMap(e.target.checked)}
                        sx={{
                          color: '#39585C',
                          '&.Mui-checked': {
                            color: '#39585C',
                          },
                        }}
                      />
                    }
                    label='Map View'
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={includeCards}
                        onChange={e => setIncludeCards(e.target.checked)}
                        sx={{
                          color: '#39585C',
                          '&.Mui-checked': {
                            color: '#39585C',
                          },
                        }}
                      />
                    }
                    label='All Analysis Cards'
                  />
                </Box>
              </Box>
            </Box>
          )}

          {activeTab === 1 && (
            <Box
              sx={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: '1.125rem',
                    fontWeight: 600,
                    marginBottom: '0.5rem',
                  }}
                >
                  Share Analysis
                </Typography>
                <Typography sx={{ fontSize: '0.875rem', color: '#6B7280' }}>
                  Generate a shareable link with optional password protection
                </Typography>
              </Box>

              <Box>
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    marginBottom: '0.75rem',
                  }}
                >
                  Permissions
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.5rem',
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={allowDownload}
                        onChange={e => setAllowDownload(e.target.checked)}
                        sx={{
                          color: '#39585C',
                          '&.Mui-checked': {
                            color: '#39585C',
                          },
                        }}
                      />
                    }
                    label='Allow recipients to download'
                  />
                </Box>
              </Box>

              {linkError && (
                <Alert severity='error' sx={{ marginBottom: '1rem' }}>
                  {linkError}
                </Alert>
              )}
              {pdfError && (
                <Alert severity='error' sx={{ marginBottom: '1rem' }}>
                  {pdfError}
                </Alert>
              )}

              {!shareLink && (
                <Button
                  variant='contained'
                  fullWidth
                  startIcon={
                    generatingLink ? (
                      <CircularProgress size={16} color='inherit' />
                    ) : (
                      <Link2 size={16} />
                    )
                  }
                  onClick={handleGenerateLink}
                  disabled={generatingLink}
                  sx={{
                    textTransform: 'none',
                    backgroundColor: '#39585C',
                    '&:hover': {
                      backgroundColor: '#2d4649',
                    },
                    '&:disabled': {
                      backgroundColor: '#9CA3AF',
                    },
                  }}
                >
                  {generatingLink ? 'Generating...' : 'Generate Share Link'}
                </Button>
              )}

              {shareLink && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.75rem',
                  }}
                >
                  <Typography sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                    Share Link
                  </Typography>
                  <Box sx={{ display: 'flex', gap: '0.5rem' }}>
                    <TextField
                      fullWidth
                      value={shareLink}
                      readOnly
                      size='small'
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#F9FAFB',
                          borderRadius: '0.75rem',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#E5E7EB',
                        },
                      }}
                    />
                    <IconButton
                      onClick={handleCopyLink}
                      sx={{
                        border: '1px solid #E5E7EB',
                        borderRadius: '0.5rem',
                        '&:hover': {
                          borderColor: '#39585C',
                          backgroundColor: '#F0F4F5',
                        },
                      }}
                    >
                      {linkCopied ? (
                        <Check size={16} color='#22C55E' />
                      ) : (
                        <Copy size={16} />
                      )}
                    </IconButton>
                  </Box>
                  <Box sx={{ display: 'flex', gap: '0.5rem' }}>
                    <Button
                      variant='outlined'
                      fullWidth
                      startIcon={<Mail size={16} />}
                      sx={{
                        textTransform: 'none',
                        borderColor: '#39585C',
                        color: '#39585C',
                        '&:hover': {
                          borderColor: '#2d4649',
                          backgroundColor: '#F0F4F5',
                        },
                      }}
                    >
                      Email Link
                    </Button>
                    <Button
                      variant='outlined'
                      fullWidth
                      startIcon={<Link2 size={16} />}
                      onClick={handleGenerateLink}
                      sx={{
                        textTransform: 'none',
                        borderColor: '#39585C',
                        color: '#39585C',
                        '&:hover': {
                          borderColor: '#2d4649',
                          backgroundColor: '#F0F4F5',
                        },
                      }}
                    >
                      New Link
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions
        sx={{ padding: '1rem 1.5rem', borderTop: '1px solid #E5E7EB' }}
      >
        {activeTab === 0 && (
          <>
            <Button
              onClick={onClose}
              sx={{
                textTransform: 'none',
                color: '#39585C',
                borderColor: '#39585C',
                '&:hover': {
                  borderColor: '#2d4649',
                  backgroundColor: '#F0F4F5',
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant='contained'
              startIcon={
                pdfGenerating ? (
                  <CircularProgress size={16} color='inherit' />
                ) : (
                  <Download size={16} />
                )
              }
              onClick={handleExport}
              disabled={pdfGenerating}
              sx={{
                textTransform: 'none',
                backgroundColor: '#39585C',
                '&:hover': {
                  backgroundColor: '#2d4649',
                },
                '&:disabled': {
                  backgroundColor: '#9CA3AF',
                },
              }}
            >
              {pdfGenerating ? 'Generating PDF...' : 'Export PDF'}
            </Button>
          </>
        )}
        {activeTab === 1 && (
          <Button
            onClick={onClose}
            fullWidth
            sx={{
              textTransform: 'none',
              color: '#39585C',
              borderColor: '#39585C',
              '&:hover': {
                borderColor: '#2d4649',
                backgroundColor: '#F0F4F5',
              },
            }}
          >
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ExportShareMenu;
